import { useMemo, useState } from "react";

import BlockRounded from "@mui/icons-material/BlockRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { Box, IconButton, Popover } from "@mui/material";
import { format, isSameMonth, isToday } from "date-fns";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import {
  READINESS,
  READINESS_ORDER,
} from "../../components/closingReadiness/closingReadinessUtils";
import {
  C,
  MONO,
  dayKey,
  entryLabel,
  entryTimeLabel,
  reasonStyle,
} from "./coverageUtils";
// Loan-closing helpers reused from the legacy loan-closings calendar (kept as a
// sibling screen) so the two stay consistent.
import ClosingChip from "../LoanClosingsCalendarScreen/ClosingChip";
import {
  deriveStatus,
  formatCompactDollar,
  loanId,
  sumLoanAmounts,
} from "../LoanClosingsCalendarScreen/closingCalendarUtils";

const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

// How many chips a cell shows before "+N more": a month cell is short, a week
// column has the whole viewport.
const MAX_VISIBLE = { month: 4, week: 14 };

// An out-of-office entry chip: reason bar · person · time.
const CoverageChip = ({ entry, onClick }) => {
  const s = reasonStyle(entry?.reason);
  return (
    <Flx
      g={0.75}
      onClick={(e) => {
        e.stopPropagation();
        onClick(entry);
      }}
      sx={{
        alignItems: "stretch",
        mb: "3px",
        px: 0.85,
        py: 0.5,
        borderRadius: "6px",
        background: s.bg,
        color: s.c,
        border: "1px solid transparent",
        cursor: "pointer",
        minWidth: 0,
        transition: "border-color 0.1s",
        "&:hover": { borderColor: "currentColor" },
      }}
    >
      <Box sx={{ width: 3, alignSelf: "stretch", borderRadius: "2px", background: s.c, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Txt sx={{ fontSize: 11.5, fontWeight: 600, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
          {entryLabel(entry)}
        </Txt>
        <Txt sx={{ fontFamily: MONO, fontSize: 10, color: C.sub, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
          {entryTimeLabel(entry)}
        </Txt>
      </Box>
    </Flx>
  );
};

const ItemChip = ({ item, onSelectClosing, onSelectEntry, selectedId }) =>
  item.kind === "closing" ? (
    <ClosingChip loan={item.data} onClick={onSelectClosing} compact selected={selectedId != null && loanId(item.data) === selectedId} />
  ) : (
    <CoverageChip entry={item.data} onClick={onSelectEntry} />
  );

const HolidayBadge = ({ holiday, onClick }) => (
  <Flx
    ac
    g={0.4}
    onClick={onClick ? (e) => { e.stopPropagation(); onClick(holiday); } : undefined}
    sx={{
      mb: "3px",
      px: 0.7,
      py: 0.4,
      borderRadius: "6px",
      background: holiday?.wireBlocked ? C.blockBg : C.lineSoft,
      color: holiday?.wireBlocked ? C.block : C.sub,
      cursor: onClick ? "pointer" : "default",
      minWidth: 0,
    }}
  >
    {holiday?.wireBlocked && <BlockRounded sx={{ fontSize: 12, flexShrink: 0 }} />}
    <Txt sx={{ fontSize: 10.5, fontWeight: 700, color: holiday?.wireBlocked ? C.block : C.sub, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
      {holiday?.wireBlocked ? `Wires blocked · ${holiday?.name || "Holiday"}` : holiday?.name || "Holiday"}
    </Txt>
  </Flx>
);

// A thin segmented bar: the day's pending fundings split by readiness level,
// worst first. Reads the day's mix without opening it.
export const ReadinessBar = ({ closings, height = 3, sx }) => {
  const active = closings.filter((c) => !c?.closed);
  if (!active.length) return null;
  const counts = {};
  active.forEach((c) => {
    const k = deriveStatus(c);
    counts[k] = (counts[k] || 0) + 1;
  });
  return (
    <Flx
      sx={{
        height,
        borderRadius: `${height}px`,
        overflow: "hidden",
        background: C.lineSoft,
        flexShrink: 0,
        ...sx,
      }}
    >
      {READINESS_ORDER.filter((k) => k !== "closed" && counts[k]).map((k) => (
        <Box key={k} sx={{ width: `${(counts[k] / active.length) * 100}%`, background: READINESS[k].c }} />
      ))}
    </Flx>
  );
};

const DayCell = ({
  date,
  inMonth,
  view,
  closings,
  entries,
  holiday,
  layers,
  onSelectClosing,
  selectedId,
  onSelectEntry,
  onSelectHoliday,
  onOpenDay,
  isLastCol,
  isLastRow,
}) => {
  const [moreAnchor, setMoreAnchor] = useState(null);
  const today = isToday(date);
  const showHoliday = layers.holidays && holiday;
  const wireBlocked = showHoliday && holiday?.wireBlocked;

  // Closings first, then out-of-office, gated by the active layers.
  const items = useMemo(() => {
    const list = [];
    if (layers.closings) closings.forEach((c) => list.push({ kind: "closing", data: c, id: `c${loanId(c)}` }));
    if (layers.coverage) entries.forEach((e) => list.push({ kind: "ooo", data: e, id: `o${e._id}` }));
    return list;
  }, [layers, closings, entries]);

  const shownClosings = layers.closings ? closings : [];
  const active = shownClosings.filter((c) => !c?.closed);
  const visible = items.slice(0, MAX_VISIBLE[view] || MAX_VISIBLE.month);
  const overflow = items.length - visible.length;
  const total = active.length ? formatCompactDollar(sumLoanAmounts(active)) : null;

  return (
    <Box
      sx={{
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        p: "6px 7px 4px",
        borderRight: isLastCol ? "none" : `1px solid ${C.line}`,
        borderBottom: isLastRow ? "none" : `1px solid ${C.line}`,
        // Today sits on the brand's faintest teal wash (the same tint the
        // selected-state system uses for hover fills).
        background: wireBlocked ? "#fef6f5" : today ? C.todayBg : inMonth ? C.surface : "#fafafa",
        display: "flex",
        flexDirection: "column",
        transition: "background 0.12s",
        "&:hover": { background: wireBlocked ? "#fef6f5" : today ? C.todayBg : inMonth ? "#fafbfc" : "#f7f7f8" },
      }}
    >
      <Flx ac g={0.9} sx={{ mb: "5px", flexShrink: 0 }}>
        <Box
          onClick={() => onOpenDay?.(date)}
          title="Open day view"
          sx={{
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            fontSize: 12.5,
            fontWeight: today ? 700 : 500,
            color: today ? "#fff" : inMonth ? C.sub : "#c3c9d1",
            background: today ? C.accent : "transparent",
            cursor: onOpenDay ? "pointer" : "default",
            flexShrink: 0,
            "&:hover": { background: today ? C.accent : C.lineSoft },
          }}
        >
          {format(date, "d")}
        </Box>
        {active.length > 0 && (
          <Txt sx={{ fontSize: 10.5, color: C.faint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            <Box component="b" sx={{ color: C.sub, fontWeight: 600 }}>{active.length}</Box>
            {" · "}
            {total}
          </Txt>
        )}
        {layers.coverage && entries.length > 0 && (
          <Txt sx={{ fontSize: 10.5, color: C.faint, whiteSpace: "nowrap", ml: "auto" }}>
            {entries.length} out
          </Txt>
        )}
      </Flx>

      <Box sx={{ minWidth: 0, flex: 1, minHeight: 0, overflow: "hidden" }}>
        {showHoliday && <HolidayBadge holiday={holiday} onClick={onSelectHoliday} />}
        {visible.map((item) => (
          <ItemChip key={item.id} item={item} onSelectClosing={onSelectClosing} onSelectEntry={onSelectEntry} selectedId={selectedId} />
        ))}
        {overflow > 0 && (
          <Box
            onClick={(e) => setMoreAnchor(e.currentTarget)}
            sx={{ px: 0.75, py: "3px", mt: "1px", fontSize: 11, fontWeight: 600, color: C.sub, cursor: "pointer", borderRadius: "5px", width: "fit-content", "&:hover": { background: C.lineSoft, color: C.accent } }}
          >
            +{overflow} more
          </Box>
        )}
      </Box>

      {/* Peek: the whole day, grouped by readiness level. */}
      <Popover
        open={Boolean(moreAnchor)}
        anchorEl={moreAnchor}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 340, maxHeight: 440, borderRadius: "14px", border: `1px solid ${C.line}`, boxShadow: "0 12px 44px -8px rgba(15,23,42,.25)", display: "flex", flexDirection: "column" } } }}
      >
        <Flx ac g={1.25} sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
          <Box>
            <Txt sx={{ fontSize: 15, fontWeight: 700, color: C.ink, display: "block" }}>
              {format(date, "EEEE, MMMM d")}
            </Txt>
            <Txt sx={{ fontSize: 12, color: C.faint }}>
              {active.length} active{total ? ` · ${total}` : ""}
              {layers.coverage && entries.length ? ` · ${entries.length} out` : ""}
            </Txt>
          </Box>
          <IconButton size="small" onClick={() => setMoreAnchor(null)} sx={{ ml: "auto", color: C.faint }}>
            <CloseRounded sx={{ fontSize: 16 }} />
          </IconButton>
        </Flx>
        <Box sx={{ flex: 1, overflowY: "auto", px: 1.25, pb: 1.5 }}>
          {READINESS_ORDER.map((k) => {
            const group = items.filter((it) => it.kind === "closing" && deriveStatus(it.data) === k);
            if (!group.length) return null;
            return (
              <Box key={k}>
                <Txt sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.faint, display: "block", px: 0.75, pt: 1.1, pb: 0.6 }}>
                  {READINESS[k].label} · {group.length}
                </Txt>
                {/* The same compact chip the day cell draws — the popover is
                    the cell's overflow, not a different view of the loan. */}
                {group.map((item) => (
                  <ClosingChip
                    key={item.id}
                    loan={item.data}
                    compact
                    selected={selectedId != null && loanId(item.data) === selectedId}
                    onClick={(l) => { setMoreAnchor(null); onSelectClosing(l); }}
                  />
                ))}
              </Box>
            );
          })}
          {items.some((it) => it.kind === "ooo") && (
            <Box>
              <Txt sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.faint, display: "block", px: 0.75, pt: 1.1, pb: 0.6 }}>
                Out of office · {items.filter((it) => it.kind === "ooo").length}
              </Txt>
              {items
                .filter((it) => it.kind === "ooo")
                .map((item) => (
                  <CoverageChip
                    key={item.id}
                    entry={item.data}
                    onClick={(en) => { setMoreAnchor(null); onSelectEntry(en); }}
                  />
                ))}
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

// The month / week grid. `days` is the run of dates to plot (a multiple of 7,
// Sunday-first); `anchorDate` decides which days read as "in" the period.
const CoverageGridView = ({
  view = "month",
  days,
  anchorDate,
  closingsByDay,
  byDay,
  holidayByDay,
  layers,
  onSelectClosing,
  selectedId,
  onSelectEntry,
  onSelectHoliday,
  onOpenDay,
}) => {
  const rows = Math.max(1, Math.ceil(days.length / 7));
  // A month row needs room for its header + four chips; a week column takes
  // the whole viewport.
  const minRow = view === "week" ? 420 : 150;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1, width: "100%" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: `1px solid ${C.line}`, flexShrink: 0 }}>
        {WEEKDAYS.map((w) => (
          <Box key={w} sx={{ minWidth: 0, py: "7px", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: C.faint, textAlign: "center" }}>
            {w}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gridTemplateRows: `repeat(${rows}, minmax(${minRow}px, 1fr))`,
        }}
      >
        {days.map((date, i) => {
          const key = dayKey(date);
          return (
            <DayCell
              key={key}
              date={date}
              view={view}
              inMonth={view === "week" ? true : isSameMonth(date, anchorDate)}
              closings={closingsByDay.get(key) || []}
              entries={byDay.get(key) || []}
              holiday={holidayByDay.get(key) || null}
              layers={layers}
              onSelectClosing={onSelectClosing}
              selectedId={selectedId}
              onSelectEntry={onSelectEntry}
              onSelectHoliday={onSelectHoliday}
              onOpenDay={onOpenDay}
              isLastCol={i % 7 === 6}
              isLastRow={i >= days.length - 7}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default CoverageGridView;
